import React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import { v4 as uuidv4 } from 'uuid'
import { Box } from '@mui/material'
import { ButtonProps } from '@mui/material/Button'

import Txt from './Txt'
import Button from './Button'
import IconButton from './IconButton'
import { DIALOG_BACKGROUND_ZINDEX, DIALOG_ZINDEX } from '../consts'

import PortalProvider from './PortalProvider'
import { BoxProps } from '@mui/system'

type DialogProps = {
	title?: string
	description?: string
	renderDescription?: () => React.ReactNode
	onClose?: () => void
	renderContent?: (val: {
		dismissPortal: () => void
		onSuccess?: () => void
		id?: string
	}) => JSX.Element
	dismissPortal: () => void
	buttons?: { id: string; label: string; onClick?: () => void; variant?: ButtonProps['variant'] }[]
	width?: string
	backgroundColor?: string
	wrapperStyle?: { [key: string]: string | number }
	isLocked?: boolean
	onSuccess?: () => void
	isMarkdown?: boolean
}

const Background = styled(Box)(() => ({
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	position: 'fixed',
	backgroundColor: 'grey',
	opacity: '50%',
	zIndex: DIALOG_BACKGROUND_ZINDEX,
}))

const ContentWrapper = styled(Box)(() => ({
	top: 0,
	left: 0,
	bottom: 0,
	right: 0,
	position: 'fixed',
	zIndex: DIALOG_ZINDEX,
	overflow: 'scroll',
}))

export const DialogBody = ({
	title,
	description,
	renderDescription,
}: {
	title?: string
	description?: string
	renderDescription?: () => React.ReactNode
}) => {
	return (
		<>
			<Txt m={'0 0 1rem 0'} fontWeight="bold" variant="h4">
				{title}
			</Txt>
			{renderDescription ? (
				<Box m={'0 0 1rem 0'}>{renderDescription()}</Box>
			) : (
				<Txt m={'0 0 1rem 0'} lineBreaks>
					{description}
				</Txt>
			)}
		</>
	)
}

export const DialogButtonRow = (props: BoxProps) => {
	return (
		<Box
			width="100%"
			m={'2rem 0 0'}
			display="flex"
			flexDirection="row"
			justifyContent="flex-end"
			alignItems="center"
			flexWrap="wrap"
			rowGap="1rem"
			{...props}
		>
			{props.children}
		</Box>
	)
}

// Solange ein Dialog offen ist, scrollt die Seite dahinter nicht mit — sonst wandert der Inhalt
// unter dem Dialog weg, während man in ihm liest. Der Dialog selbst scrollt weiter (ContentWrapper).
// Gezählt, weil Dialoge übereinander liegen können: erst der letzte gibt die Seite wieder frei, und
// zwar auf die Werte, die vor dem ersten galten.
const bodyScrollLock = { count: 0, overflow: '', paddingRight: '' }

const Dialog = ({
	renderContent,
	title,
	description,
	renderDescription,
	onClose,
	dismissPortal,
	buttons,
	isLocked,
	onSuccess,
	isMarkdown,
	width,
}: DialogProps): JSX.Element => {
	React.useEffect(() => {
		const { body } = document
		if (bodyScrollLock.count === 0) {
			// Der Platz des verschwindenden Scrollbalkens bleibt als Innenabstand stehen, damit der
			// Seiteninhalt beim Öffnen nicht zur Seite springt.
			const scrollbarWidth = window.innerWidth - body.clientWidth
			const currentPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0
			bodyScrollLock.overflow = body.style.overflow
			bodyScrollLock.paddingRight = body.style.paddingRight
			body.style.overflow = 'hidden'
			body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`
		}
		bodyScrollLock.count += 1
		return () => {
			bodyScrollLock.count -= 1
			if (bodyScrollLock.count === 0) {
				body.style.overflow = bodyScrollLock.overflow
				body.style.paddingRight = bodyScrollLock.paddingRight
			}
		}
	}, [])

	const handleClose = React.useCallback(() => {
		onClose?.()
		dismissPortal()
	}, [dismissPortal, onClose])

	const handleClick = React.useCallback(
		(buttonId: string) => {
			buttons?.find((button) => button.id === buttonId)?.onClick?.()
			dismissPortal()
		},
		[buttons, dismissPortal]
	)

	const handleStopPropagation = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation()
	}, [])

	return (
		<Box>
			<ContentWrapper onClick={isLocked ? undefined : handleClose} id="dialog" p={'1rem'}>
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					width="100%"
					minHeight="100%"
					flex="1"
				>
					<Box
						display="flex"
						bgcolor="background.paper"
						borderRadius="1rem"
						padding={renderContent ? undefined : '2rem'}
						boxShadow="24"
						onClick={handleStopPropagation}
						flexDirection="column"
						overflow="hidden"
						position="relative"
						minWidth="300px"
						maxWidth={width ?? '700px'}
					>
						{renderContent ? (
							renderContent({ dismissPortal: handleClose, onSuccess: onSuccess })
						) : (
							<DialogBody
								title={title}
								description={description}
								renderDescription={renderDescription}
							/>
						)}

						{buttons?.length ? (
							<DialogButtonRow>
								{buttons.map(({ id: buttonId, label, onClick, variant }) => (
									<Button
										m={'0 0 0 1rem'}
										key={buttonId}
										id={buttonId}
										onClick={onClick ? handleClick : handleClose}
										variant={variant}
									>
										{label}
									</Button>
								))}
							</DialogButtonRow>
						) : null}
						{isLocked ? null : (
							<Box
								position="absolute"
								right="0"
								top="0"
								display="flex"
								alignItems="center"
								justifyContent="center"
								p="1rem"
							>
								<IconButton iconName="CloseOutlined" onClick={handleClose} />
							</Box>
						)}
					</Box>
				</Box>
			</ContentWrapper>
			<Background />
		</Box>
	)
}

const dialogStack: string[] = []
Dialog.render = (props: Omit<DialogProps, 'dismissPortal'>, id?: string): void => {
	if (!id) {
		id = uuidv4() as string
		dialogStack.push(id)
	} else {
		if (!dialogStack.includes(id)) {
			dialogStack.push(id)
		}
	}
	PortalProvider.render(id, Dialog, props)
}
Dialog.unmount = (id?: string) => {
	if (!id) id = dialogStack.pop()
	if (id) PortalProvider.unmount(id)
}

export default Dialog

export const renderErrorDialog = (error: string): void => {
	Dialog.render({
		title: 'Fehler',
		description: `Es ist ein unerwarteter Fehler aufgetreten: ${error}`,
	})
}
